import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/domains/common/Common';
import { GeniusLivePlayerState } from './GeniusLivePlayerState';
import { GeniusLivePlayerContainer, GeniusLivePlayerWrapper } from './GeniusLivePlayer.style';
import { GENIUS_MESSAGE_BUS_EVENT } from './geniusLivePlayer.types';

interface GeniusLivePlayerPropsType {
    eventId: number;
    onClaimsLeadChange?: (claimsLead: boolean) => void;
}

export const GeniusLivePlayer = observer(
    'GeniusLivePlayer',
    ({ eventId, onClaimsLeadChange }: GeniusLivePlayerPropsType) => {
        const common = useCommon();
        const [state] = useState(() => new GeniusLivePlayerState(common, eventId));
        const scriptRef = useRef<HTMLScriptElement | null>(null);
        const listenerRef = useRef<((event: Event) => void) | null>(null);

        const metadataResult = state.metadataResult;

        useEffect(() => {
            state.syncUiStatusFromMetadata();
        }, [state, metadataResult.type]);

        useEffect(() => {
            if (onClaimsLeadChange === undefined) {
                return;
            }

            onClaimsLeadChange(state.claimsLead);
        }, [onClaimsLeadChange, state.claimsLead]);

        useEffect(() => {
            if (!state.canMountPlayer) {
                return;
            }

            const src = state.getWidgetLoaderSrc();
            if (src === null) {
                return;
            }

            const handleEvent = (event: Event): void => {
                void state.handleGeniusMessage(event);
            };

            listenerRef.current = handleEvent;
            window.addEventListener(GENIUS_MESSAGE_BUS_EVENT, handleEvent);

            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.id = `genius-live-player-loader-${eventId}`;
            document.body.appendChild(script);
            scriptRef.current = script;

            return (): void => {
                if (listenerRef.current !== null) {
                    window.removeEventListener(GENIUS_MESSAGE_BUS_EVENT, listenerRef.current);
                    listenerRef.current = null;
                }

                state.closePlayer();

                if (scriptRef.current !== null) {
                    scriptRef.current.remove();
                    scriptRef.current = null;
                }
            };
        }, [state, state.canMountPlayer, eventId]);

        if (metadataResult.type === 'loading' || !state.canMountPlayer) {
            return null;
        }

        return (
            <GeniusLivePlayerWrapper
                maxSizePercentage={state.maxPlayerSizePercentage}
                isVisible={state.claimsLead}
                data-test='genius-live-player'
            >
                <GeniusLivePlayerContainer id={state.containerId} />
            </GeniusLivePlayerWrapper>
        );
    }
);
